import { action, makeObservable, observable, runInAction } from "mobx";
import {
    ErrorStore,
    ProjectsStore,
    UserProfileStore,
} from "../../common/stores";
import { TaskCommentModel } from "../../tasks/types";
import DashboardService from "../services/DashboardService";
import {
    ActivitiesPeriodEnum,
    Activity,
    ActivityByProjects,
    AssignmentMsg,
    UpdatedActivitiesPushMsg,
} from "../types/Activity";
import tasksPushClient from "../../tasks/services/TaskPushClient";
import { Observable } from "rxjs";
import WidgetTypes, {
    DEFAULT_DASHBOARD_VIEW_KEY,
    WidgetView,
} from "../models/WidgetTypes";
import ListWidget from "../models/ListWidget";
import GroupedWidget from "../models/GroupedWidget";
import PieChartWidget from "../models/PieChartWidget";
import { UserDashboardSettings } from "../types/DashboardSettings";
import { DashboardWidgetType } from "src/modules/administration/types/DashboardWidget";
import { TilePosition } from "@progress/kendo-react-layout";

type CustomTilePosition = TilePosition & { id: string };
export default class DashboardStore {
    assignedTasks: string[];

    comments: TaskCommentModel[];

    activities: ActivityByProjects = { AllProjects: [], SelectedProject: [] };

    widgets: WidgetTypes[] = [];

    views: WidgetView[] = [];

    isInitialLoading: boolean;

    isLoading: boolean;

    reloadingLastActivities: boolean = false;

    selectedActivitiesProjectId: string | null;

    currentView: string = DEFAULT_DASHBOARD_VIEW_KEY;

    activitiesPeriod: ActivitiesPeriodEnum = ActivitiesPeriodEnum.Today;

    private newComments: Observable<TaskCommentModel & { projectId: string }>;

    constructor(
        private dashboardService: DashboardService,
        private userProfileStore: UserProfileStore,
        private projectsStore: ProjectsStore,
        private errorStore: ErrorStore,
    ) {
        makeObservable<DashboardStore>(this, {
            assignedTasks: observable,
            activities: observable,
            widgets: observable,
            isLoading: observable,
            isInitialLoading: observable,
            reloadingLastActivities: observable,
            selectedActivitiesProjectId: observable,
            currentView: observable,
            views: observable,
            setIsLoading: action,
            setIsInitialLoading: action,
            setIsReloadingLastActivities: action,
            handleNewCommentMsg: action.bound,
            handleDeletedCommentMsg: action.bound,
            loadActivities: action.bound,
            refreshDashboard: action.bound,
            updateDashboardActivities: action.bound,
            updateTaskAssigmentActivities: action.bound,
            setSelectedActivitiesProjectId: action,
            handleActivitiesPeriodChange: action.bound,
            handleActivitiesProjectChange: action.bound,
            createView: action,
            setCurrentView: action,
            changeCurrentView: action,
        });
        const { pushClient } = tasksPushClient;

        const newCommentObs = pushClient
            .createTaskNewCommentListener()
            .publish();
        this.newComments = newCommentObs;
        newCommentObs.connect();
        this.newComments.subscribe(this.handleNewCommentMsg);

        const deletedCommentObs = pushClient
            .createTaskDeleteCommentListener()
            .publish();
        deletedCommentObs.subscribe(this.handleDeletedCommentMsg);
        deletedCommentObs.connect();

        const updatedActivitiesObs = pushClient
            .createDashboardActivitiesListener()
            .publish();
        updatedActivitiesObs.subscribe(this.updateDashboardActivities);
        updatedActivitiesObs.connect();

        const newTaskAssingmentObs = pushClient
            .createTaskAssignmentListener()
            .publish();
        newTaskAssingmentObs.subscribe(this.updateTaskAssigmentActivities);
        newTaskAssingmentObs.connect();
    }

    get allProjectsActivities() {
        return this.activities.AllProjects;
    }

    get selectedProjectActivities() {
        return this.activities.SelectedProject;
    }

    get projects() {
        return this.projectsStore.projects ?? [];
    }

    get regulardWidgets() {
        return this.widgets.filter(
            (w) => w.widgetType !== DashboardWidgetType.Grouped,
        );
    }

    get groupedWidgets() {
        return this.widgets.filter(
            (w) => w.widgetType === DashboardWidgetType.Grouped,
        );
    }

    // TODO: Revise this argument. May we don't need it
    async loadActivities(projectId: string | null = null) {
        this.setIsReloadingLastActivities(true);
        const selectedProjectId = projectId ?? this.projects[0]?.id;
        if (selectedProjectId) {
            const resp = await this.dashboardService.getActivities(
                this.activitiesPeriod,
                selectedProjectId,
            );
            resp.map((r) => {
                this.activities = r;
            });
        }
        this.setIsReloadingLastActivities(false);
    }

    handleActivitiesProjectChange(projectId: string) {
        this.setSelectedActivitiesProjectId(projectId);
        this.loadActivities(projectId);
    }

    handleActivitiesPeriodChange(period: string) {
        this.setActivitiesPeriod(ActivitiesPeriodEnum[period]);
        this.loadActivities(this.selectedActivitiesProjectId);
    }

    setSelectedActivitiesProjectId(projectId: string) {
        this.selectedActivitiesProjectId = projectId;
    }

    setActivitiesPeriod(period: ActivitiesPeriodEnum) {
        this.activitiesPeriod = period;
    }

    handleNewCommentMsg(comment: TaskCommentModel & { projectId: string }) {
        const allProjectsActivities = this.activities.AllProjects.find(
            (a) => a.activityType === "NewComments",
        );
        const selectedProjectsActivities = this.activities.SelectedProject.find(
            (a) => a.activityType === "NewComments",
        );

        const { projectId } = comment;
        const hasAccessToProject = this.projectsStore.projects.some(
            (p) => p.id === projectId,
        );
        if (hasAccessToProject) {
            allProjectsActivities!.ids.push(comment.id);
            if (projectId === this.selectedActivitiesProjectId) {
                selectedProjectsActivities!.ids.push(comment.id);
            }
        }
    }

    handleDeletedCommentMsg(id: string) {
        const activities = [
            ...this.activities.AllProjects,
            ...this.activities.SelectedProject,
        ].filter((a) => a.activityType === "NewComments")!;
        activities.forEach(
            (activity) => (activity.ids = activity.ids.filter((i) => i !== id)),
        );
    }

    updateDashboardActivities(activitiesMsg: UpdatedActivitiesPushMsg) {
        if (activitiesMsg.deletedComments.length) {
            const activity = this.activities.AllProjects.find(
                (a) => a.activityType === "NewComments",
            )!;
            activity.ids = activity.ids.filter(
                (t) => !activitiesMsg.deletedComments.includes(t),
            );
        }
        if (activitiesMsg.deletedTask) {
            const activity = this.activities.AllProjects.find(
                (a) => a.activityType === "AssignedTasks",
            )!;
            activity.ids = activity.ids.filter(
                (t) => activitiesMsg.deletedTask !== t,
            );
        }
        if (activitiesMsg.deletedTaskErrors.length) {
            const activity = this.activities.AllProjects.find(
                (a) => a.activityType === "FailedMessages",
            )!;
            activity.ids = activity.ids.filter(
                (t) => !activitiesMsg.deletedTaskErrors.includes(t),
            );
        }
    }

    updateTaskAssigmentActivities(activityMsg: AssignmentMsg) {
        const allProjectsActivity = this.activities.AllProjects.find(
            (a) => a.activityType === "AssignedTasks",
        )!;
        const selectedProjectActivity = this.activities.SelectedProject.find(
            (a) => a.activityType === "AssignedTasks",
        )!;

        const { currentUserId } = this.userProfileStore;

        const updateActivity = (activity: Activity) => {
            if (
                activityMsg.userId === currentUserId &&
                !activity.ids.includes(activityMsg.taskId)
            ) {
                activity.ids.push(activityMsg.taskId);
            } else if (
                activityMsg.userId !== currentUserId &&
                activity.ids.includes(activityMsg.taskId)
            ) {
                activity.ids = activity.ids.filter(
                    (t) => !activityMsg.taskId.includes(t),
                );
            }
        };

        updateActivity(allProjectsActivity);
        updateActivity(selectedProjectActivity);
    }

    async loadInitialData() {
        this.setIsInitialLoading(true);
        const promises = [
            this.dashboardService.getWidgets(this.currentView),
            this.dashboardService.getUserSettings(),
        ] as [
            Promise<{
                widgets: (ListWidget | PieChartWidget | GroupedWidget)[];
                views: WidgetView[];
            }>,
            Promise<UserDashboardSettings | null>,
        ];
        const resp = await Promise.all(promises);
        this.widgets = resp[0].widgets;
        this.views = resp[0].views;
        const userSettings = resp[1];
        if (userSettings) {
            this.setActivitiesPeriod(userSettings.activitiesSelectedPeriod);
            this.setSelectedActivitiesProjectId(
                userSettings.activitiesSelectedProject,
            );
        } else {
            this.setActivitiesPeriod(ActivitiesPeriodEnum.Today);
            this.setSelectedActivitiesProjectId(this.projects[0]?.id);
        }
        this.loadActivities(userSettings?.activitiesSelectedProject);
        this.setIsInitialLoading(false);
    }

    async refreshDashboard() {
        this.setIsLoading(true);
        const promises = [
            this.dashboardService.getWidgets(this.currentView),
            this.currentView === DEFAULT_DASHBOARD_VIEW_KEY
                ? this.loadActivities(this.selectedActivitiesProjectId)
                : Promise.resolve(),
        ] as [
            Promise<{
                widgets: (ListWidget | PieChartWidget | GroupedWidget)[];
                views: WidgetView[];
            }>,
            Promise<void>,
        ];
        const resp = await Promise.all(promises);
        this.widgets = resp[0].widgets;
        this.views = resp[0].views;
        this.setIsLoading(false);
    }

    setIsLoading(isLoading: boolean) {
        this.isLoading = isLoading;
    }

    setIsInitialLoading(isLoading: boolean) {
        this.isInitialLoading = isLoading;
    }

    setIsReloadingLastActivities(isLoading: boolean) {
        this.reloadingLastActivities = isLoading;
    }

    setCurrentView(name: string) {
        this.currentView = name;
    }

    getWidgetsCountInView() {
        return this.currentView === DEFAULT_DASHBOARD_VIEW_KEY
            ? this.widgets.length + 1
            : this.widgets.length;
    }

    async activateWidget(id: string) {
        const currentView = this.currentView;
        const widget = await this.dashboardService.activateWidget(
            id,
            this.currentView,
        );
        this.addWidgetToLocalStorage(id);
        const widgets = this.widgets.slice();
        widgets.push(widget);
        const view = this.views.find((v) => v.name === currentView)!;
        view.widgetIds.push(widget.id);
        runInAction(() => (this.widgets = widgets));
    }

    async changeCurrentView(name: string) {
        this.setCurrentView(name);
        this.setIsLoading(true);
        const userWidgetsData = await this.dashboardService.getWidgets(name);
        this.widgets = userWidgetsData.widgets;
        this.setIsLoading(false);
    }

    async createView(name: string, widgets: string[]) {
        this.setIsLoading(true);
        const addedWidgets = await this.dashboardService.createView(
            name,
            widgets,
        );
        this.views.push({ name, widgetIds: widgets });
        this.widgets = addedWidgets;
        this.setCurrentView(name);
        this.setIsLoading(false);
    }

    async updateView(oldName: string, newName: string, widgets: string[]) {
        const resp = await this.dashboardService.updateView(
            oldName,
            newName,
            widgets,
        );
        if (resp.isOk()) {
            const editableView = this.views.find((v) => v.name === oldName)!;
            const widgetsNotUpdated =
                editableView.widgetIds.length === widgets.length &&
                editableView.widgetIds.every((id) => widgets.includes(id));

            const storageItems = localStorage.getItem(oldName);

            if (oldName !== newName && storageItems) {
                localStorage.setItem(newName, storageItems);
                localStorage.removeItem(oldName);
            }
            if (this.currentView === oldName) {
                this.setCurrentView(newName);
            }
            editableView.name = newName;

            if (storageItems) {
                const parsedItems = JSON.parse(
                    storageItems,
                ) as CustomTilePosition[];
                for (const item of parsedItems) {
                    if (!widgets.includes(item.id)) {
                        this.removeWidgetFromLocalStorage(item.id);
                    }
                }
                for (const widgetId of widgets) {
                    if (
                        !parsedItems.map((item) => item.id).includes(widgetId)
                    ) {
                        this.addWidgetToLocalStorage(widgetId);
                    }
                }
            }
            if (!widgetsNotUpdated) {
                this.refreshDashboard();
            }
        } else {
            resp.mapErr((err) => {
                this.errorStore.addError(err.data);
            });
        }
    }

    async deleteView(name: string) {
        const resp = await this.dashboardService.deleteView(name);
        if (resp.isOk()) {
            if (this.currentView === name) {
                this.changeCurrentView(DEFAULT_DASHBOARD_VIEW_KEY);
            }
            this.views = this.views.filter((v) => v.name !== name);
            const items = localStorage.getItem(name);
            if (items) {
                localStorage.removeItem(name);
            }
        } else {
            resp.mapErr((err) => {
                this.errorStore.addError(err.data);
            });
        }
    }

    async deactivateWidget(id: string) {
        const currentView = this.currentView;
        await this.dashboardService.deactivateWidget(id, this.currentView);
        this.removeWidgetFromLocalStorage(id);
        // TODO: Could be replaced by refreshDashboard but may be too expensive to refetch all widgets
        const widgets = this.widgets.slice();
        const index = this.widgets.findIndex((w) => w.id === id);
        widgets.splice(index, 1);
        runInAction(() => (this.widgets = widgets));
        const view = this.views.find((v) => v.name === currentView)!;
        view.widgetIds = view.widgetIds.filter((w) => w !== id);
    }

    private addWidgetToLocalStorage(widgetId: string) {
        const items = localStorage.getItem(this.currentView);
        if (items) {
            const parsedItems = JSON.parse(items);
            const nextOrder = parsedItems.length;
            const newTile = {
                rowSpan: 1,
                colSpan: 1,
                id: widgetId,
                col: (nextOrder % 3) + 1,
                order: nextOrder,
            };

            parsedItems.push(newTile);
            localStorage.setItem(this.currentView, JSON.stringify(parsedItems));
        }
    }

    private removeWidgetFromLocalStorage(widgetId: string) {
        const items = localStorage.getItem(this.currentView);
        if (items) {
            const parsedItems = JSON.parse(items) as (CustomTilePosition & {
                order?: number;
            })[];
            const index = parsedItems.findIndex((w) => w.id === widgetId);

            if (index !== -1) {
                const deletedItem = parsedItems[index];
                const deletedCol = deletedItem.col;
                const deletedOrder = deletedItem.order ?? index;

                // Remove the item
                parsedItems.splice(index, 1);

                // Find all items in the same column with greater order
                const itemsInSameCol = parsedItems
                    .map((item, idx) => ({ item, originalIndex: idx }))
                    .filter(
                        ({ item }) =>
                            item.col === deletedCol &&
                            (item.order ?? 0) > deletedOrder,
                    )
                    .sort((a, b) => (a.item.order ?? 0) - (b.item.order ?? 0));

                // Reassign order values starting from deletedOrder
                let newOrder = deletedOrder;
                itemsInSameCol.forEach(({ item }) => {
                    item.order = newOrder;
                    newOrder = (item.order ?? 0) + 3; // Increment by 3 (number of columns)
                });

                localStorage.setItem(
                    this.currentView,
                    JSON.stringify(parsedItems),
                );
            }
        }
    }
}
